<template>
  <div class="col-12">
    <div class="row">
      <div class="col-12">
        <div class="pane-header">
          <span class="icon"><icon-colored-markup /></span>
          <span class="text">{{ capitalize($t('names.spendSteering')) }}</span>
          <div class="actions">
            <div
              v-if="isDateInRange"
              id="steering-granularity"
              class="input-container">
              <div class="icon">
                <icon-clock />
              </div>
              <multiselect
                v-model="granularity"
                track-by="id"
                label="name"
                :allow-empty="false"
                :show-labels="false"
                :options="granularityOptions"
                :placeholder="'Select interval'" />
            </div>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="pane-content p-2">
          <div class="col-12">
            <div class="row">
              <div class="col-3">
                <h4>{{ capitalizeTranslation('names.dimensionType') }}</h4>
                <span
                  id="package_details_start_date"
                  class="text-muted">
                  {{
                    capitalizeTranslation(
                      dimensionType === SelectableDimensionType.DEAL ? 'names.deal' : 'names.mediaOwner',
                    )
                  }}
                </span>
              </div>
              <div class="col-3">
                <h4>{{ capitalizeTranslation('names.budgetMode') }}</h4>
                <span
                  id="package_details_start_date"
                  class="text-muted"
                  >{{
                    capitalizeTranslation(
                      budgetMode === BudgetModes.BUDGET ? 'names.budget' : 'validatorNames.impressions',
                    )
                  }}</span
                >
              </div>
            </div>
            <hr class="mt-4" />
          </div>
          <div class="row">
            <div class="col-12">
              <div
                class="m-2 mb-3 spend-steering-dimensions-details"
                style="display: flex; flex-direction: column; gap: 1rem">
                <deal-spend-steering-dimension-card
                  v-for="dimension in deal.spendSteering.spendSteeringDimensions"
                  :key="dimension.dimensionValue"
                  :dimension-value="dimension.dimensionValue"
                  :dimension-type="dimensionType"
                  :suppliers="suppliers"
                  :deal="deal"
                  :is-loading="isLoading"
                  :is-read-only="true"
                  :can-expand="steeringSummary && steeringSummary.entries.size > 0 && isDateInRange">
                  <template #controls>
                    <div
                      v-if="dimension.highPriority"
                      class="read-only-high-priority badge badge-success py-2">
                      {{ capitalizeTranslation('messages.highPriority').toUpperCase() }}
                    </div>
                    <div class="read-only-weight font-weight-bold">
                      {{ formatNumber(dimension.steeringPercentage / 100, '0.00%') }}
                    </div>
                  </template>
                  <template #expanded>
                    <div
                      class="kpis mx-3 mt-1 p-2"
                      style="background-color: white; border-radius: 1rem; border: 0.1rem solid #dee2e6">
                      <k-p-i
                        :label="budgetModeText"
                        :icon="
                          dimensionType === BudgetModes.IMPRESSIONS
                            ? 'icon-colored-impressions-delivered'
                            : 'icon-colored-ad-spend'
                        "
                        :value="
                          getDeliveryFormatted(steeringSummary.getTotalDeliveryByDimension(dimension.dimensionValue))
                        " />
                      <k-p-i
                        icon="icon-colored-pacing"
                        :label="capitalizeTranslation('names.expectedWeight')"
                        :value="formatNumber(dimension.steeringPercentage / 100, '0.00%')" />
                      <k-p-i
                        icon="icon-colored-pacing"
                        :label="capitalizeTranslation('names.actualWeight')"
                        :value="steeringSummary.getPercentageByDimension(dimension.dimensionValue)" />
                    </div>
                    <chart
                      v-if="steeringSummary.entries.size > 1"
                      template-ref="dimensionChart"
                      name="dimensionChart"
                      :mode="ChartMode.COMPACT"
                      :echarts-options="getDimensionChart(dimension)"
                      :show-future="false"
                      :theme="ChartTheme.ONE_TONE_BLUE" />
                  </template>
                </deal-spend-steering-dimension-card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as echarts from 'echarts';
  import { DateTime } from 'luxon';
  import { capitalize, computed, Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import Chart from '@/common/components/chart/Chart.vue';
  import DealSpendSteeringDimensionCard from '@/common/components/deals/form/spendSteering/DealSpendSteeringDimensionCard.vue';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import { IconColoredMarkup } from '@/common/icons/colored';
  import { IconClock } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import { ISpendSteeringDimension, SelectableDimensionType } from '@/common/models/package/SpendSteering';
  import SpendSteeringSummary from '@/common/models/package/SpendSteeringSummary';
  import { capitalizeTranslation, formatCurrency, formatNumber } from '@/globals';
  import KPI from '@/org-app/components/controls/KPI.vue';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';

  //#region Props

  interface IProps {
    deal: Package;
    suppliers: MediaOwner[];
  }

  const { deal, suppliers } = defineProps<IProps>();

  //#endregion

  //#region State

  const { getCuratedPackageSteeringSummary } = mapOrgAppActions();
  const { Packages: packagesState } = mapOrgAppState();

  //#endregion

  //#region Data

  const i18n = useI18n();
  const isLoading = ref(false);
  const steeringSummary: Ref<SpendSteeringSummary> = ref(null);
  const granularityOptions = [
    { name: capitalizeTranslation('options.reportDefinition.breakdown.daily'), id: 'DAILY' },
    { name: capitalizeTranslation('options.reportDefinition.breakdown.hourly'), id: 'HOURLY' },
  ];
  const granularity = ref(granularityOptions[0]);

  //#endregion

  //#region Computed

  const dealSpendSteering = computed(() => deal.spendSteering);

  const budgetMode = computed(() => dealSpendSteering.value?.budgetMode);

  const dimensionType = computed(() => dealSpendSteering.value?.dimensionType);

  const cumulativeSpendByDate = computed(() => steeringSummary.value.cumulativeSpendByDate);

  const dealCurrencyInformation = computed(() => deal.currencyInformation);

  const budgetModeText = computed(() =>
    i18n.t(budgetMode.value === BudgetModes.IMPRESSIONS ? 'validatorNames.impressions' : 'names.spend'),
  );

  const isDateInRange = computed(() => {
    const { start, end } = packagesState.summaryDateRange;
    // If no start or end date is provided, return the deal's hasStarted status
    // If the deal has not started, then we're not in range.
    if (!start || !end || !deal.hasStarted) {
      return deal.hasStarted;
    }
    const inputStart = DateTime.fromISO(start);
    const inputEnd = DateTime.fromISO(end);
    // Check if the input date range overlaps with the deal's date range
    // If the input dates are in the future, we won't have any data so it is also not in range.
    return inputStart <= DateTime.now() && inputStart <= deal.endDate && inputEnd >= deal.startDate;
  });

  //#endregion

  //#region Methods

  async function getSteeringSummary() {
    const dateRange = packagesState?.summaryDateRange;
    const start = dateRange?.start ?? deal.startDate;
    const end = dateRange?.end ?? deal.endDate;
    return await getCuratedPackageSteeringSummary({
      id: deal.id,
      start,
      end,
      resolution: granularity.value.id,
    });
  }

  function getDeliveryFormatted(value: number) {
    return budgetMode.value === BudgetModes.IMPRESSIONS
      ? formatNumber(value, '0,0')
      : formatCurrency(value, { currencyCode: dealCurrencyInformation.value.currency, showCurrencyCode: true });
  }

  function getDimensionChart(dimension: ISpendSteeringDimension) {
    const dataPoints = steeringSummary.value.getCumulativeSummaryByDimension(dimension.dimensionValue);
    const formatDate = (value: string) => {
      return DateTime.fromISO(value).toLocaleString(
        granularity.value.id === 'DAILY' ? DateTime.DATE_SHORT : DateTime.DATETIME_MED,
      );
    };
    return {
      grid: {
        top: 65,
        left: 30,
        right: 60,
        bottom: 60,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const param = params[0];
          const { axisValueLabel, axisValue, value } = param;

          const actualWeight = formatNumber(value, '0.00%');
          const dimensionDelivery = getDeliveryFormatted(
            dataPoints.find((x) => x.confirmedAtLocal === axisValue).totalDelivery,
          );
          // Overall delivery for all dimensions by date
          const totalDeliveryByDate = getDeliveryFormatted(cumulativeSpendByDate.value.get(axisValue));
          const cumulativeBudgetModeText = capitalizeTranslation('messages.dealSpendSteeringCumulativeBudget', [
            budgetModeText.value,
          ]);

          // Template for tooltip
          return `
        <strong>${formatDate(axisValueLabel)}</strong>
        <br/>
        ${capitalizeTranslation('names.weight')}: <strong>${actualWeight}</strong>
        <br/>
        ${capitalize(budgetModeText.value)}: <strong>${dimensionDelivery}</strong>
        <br/>
        ${cumulativeBudgetModeText}: <strong>${totalDeliveryByDate}</strong>
       `;
        },
      },
      xAxis: {
        type: 'category',
        data: dataPoints.map((x) => x.confirmedAtLocal),
        cellType: CellType.DATE_TIME,
        axisLabel: {
          formatter: (value: string) => formatDate(value),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatNumber(value, '0.00%'),
        },
        min: 0,
        max: 1,
      },
      dataZoom: [
        // Allows for both slider and inside zooming
        {
          type: 'slider',
          start: 0,
          end: 100,
          fillerColor: 'rgba(0, 153, 255, 0.15)',
          labelFormatter: '',
        },
        {
          type: 'inside',
          start: 0,
          end: 100,
          bottom: 10,
        },
      ],
      series: [
        {
          type: 'line',
          areaStyle: {
            opacity: 0.8,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(0, 102, 255, 0.35)',
              },
              {
                offset: 1,
                color: 'rgba(0, 153, 255, 0.15)',
              },
            ]),
          },
          smooth: true,
          data: dataPoints.map((x) => x.cumulativeWeight),
          itemStyle: {
            color: 'rgba(0, 102, 255, 0.5)',
          },
          markLine: {
            data: [{ yAxis: dimension.steeringPercentage / 100 }],
            label: {
              formatter: () => formatNumber(dimension.steeringPercentage / 100, '0.00%'),
              opacity: 1,
            },
            lineStyle: {
              type: 'dashed',
              color: '#ff0083',
              width: 2.25,
              opacity: 0.75,
            },
            silent: true,
            symbol: 'none',
            emphasis: {
              focus: 'series',
            },
          },
        },
      ],
    };
  }

  async function refreshSteeringSummary() {
    isLoading.value = true;
    steeringSummary.value = await getSteeringSummary();
    isLoading.value = false;
  }

  //#endregion

  //#region Watchers

  // Watch for changes in the date range
  watch(
    () => packagesState.summaryDateRange,
    async () => {
      if (isDateInRange.value) {
        await refreshSteeringSummary();
      }
    },
    { immediate: true, deep: true },
  );

  watch(
    () => granularity.value,
    async (newVal, oldVal) => {
      if (newVal !== oldVal && isDateInRange.value) {
        await refreshSteeringSummary();
      }
    },
  );

  //#endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;
</style>
